import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationUtil } from '../../shared/util/notification.util';
import { ServerErrorUtil } from '../../shared/util/server-error.util';
import { Constant } from '../../shared/common/constant';
import { ErrorResponse } from '../../shared/model/local/error/error-response';
import { ServerErrorDto } from '../../shared/model/local/error/server-error.dto';
import { ObjectUtil } from '../../shared/util/object.util';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private readonly notificationUtil: NotificationUtil,
    private readonly serverErrorUtil: ServerErrorUtil
  ) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          const { status } = error;

          if (request.headers.has(Constant.HTTP_IGNORE_ERROR)) {
            return throwError({
              handled: false,
              error: error.error,
              customErrorCode: false,
            } as ErrorResponse);
          }

          if (status === 403) {
            this.showMessage('ERROR.FORBIDDEN');
            return throwError({
              handled: true,
              error: error?.error,
            } as ErrorResponse);
          } else if (status === 401) {
            return throwError({
              handled: true,
              error: error?.error,
            } as ErrorResponse);
          } else if (status === 413) {
            this.showMessage('ERROR.FILE_SIZE_LIMIT_EXCEEDED');
            return throwError({
              handled: true,
              error: error?.error,
            } as ErrorResponse);
          } else if (status === 0 && request.body instanceof FormData) {
            this.showMessage('ERROR.FILE_SIZE_LIMIT_EXCEEDED');
          } else if (
            [400, 422].includes(status) &&
            (error.error?.errorCode || error.error?.errorDescription)
          ) {
            const errorCode = error.error?.errorCode;
            const customHandleErrorCodes =
              request.headers.getAll(Constant.HTTP_CUSTOM_HANDLE_ERROR_CODES) ||
              [];

            if (errorCode && customHandleErrorCodes.includes(errorCode)) {
              return throwError({
                handled: false,
                error: error.error,
                customErrorCode: true,
              } as ErrorResponse);
            }

            const result = this.serverErrorUtil.getServerErrorMessage(
              error,
              'ERROR.OPERATION_UNSUCCESSFUL'
            );
            this.showMessage(result.key, result.params ?? {});

            const normalizedError = ObjectUtil.plainToInstance(
              ServerErrorDto,
              error.error as object
            );
            return throwError({
              handled: true,
              error: normalizedError,
            } as ErrorResponse);
          } else {
            this.showMessage('ERROR.GENERAL_ERROR');
          }
        }

        return throwError({
          handled: true,
          error: error.error,
        } as ErrorResponse);
      })
    );
  }

  private showMessage(key: string, params: any = {}) {
    this.notificationUtil.error(key, params);
  }
}
